// Shopify integration — token-less, Headless-channel-less.
//
// Strategy:
//   1. PRODUCTS are loaded from Shopify's public `/products.json` endpoint.
//      No tokens required. Anything published to the storefront's Online
//      Store channel is returned automatically, so new products show up
//      without any code or admin changes.
//
//   2. CART is fully local (browser localStorage). We mimic the shape of
//      the previous Storefront API cart so existing UI (ProductCard,
//      ProductDetailModal, CartDrawer) keeps working unchanged.
//
//   3. CHECKOUT uses Shopify's cart-permalink URL:
//        https://{shop}.myshopify.com/cart/{variantId}:{qty},...
//      Shopify hydrates the cart server-side and walks the customer
//      through its hosted, branded checkout. Best of both worlds: fully
//      embedded site experience + real Shopify checkout.
//
// All product/variant IDs from the REST API are numeric. We store them
// as strings everywhere for safety.
//
// REST product shape is normalized at read time into the same shape the
// old GraphQL Storefront client returned, so downstream components stay
// untouched.

const DOMAIN = process.env.REACT_APP_SHOPIFY_DOMAIN;
// Same-origin proxy path. Both Netlify (`public/_redirects`) and the local
// craco dev server (see `craco.config.js`) rewrite `/shopify-proxy/*` to
// `https://{DOMAIN}/*`. This sidesteps CORS — Shopify's public storefront
// endpoints don't return Access-Control-Allow-Origin, so a direct cross-
// origin fetch from the browser is blocked.
const PRODUCTS_URL = `/shopify-proxy/products.json`;
// Checkout MUST be a top-level navigation to Shopify (cookies, session),
// so this stays as the absolute URL.
const CART_PERMALINK_BASE = `https://${DOMAIN}/cart`;
const DEFAULT_CURRENCY = "USD";

const STORAGE_KEY = "vfi_local_cart_v1";

/* ------------------------------ Normalizers ------------------------------ */

function stripHtml(html) {
  if (!html) return "";
  return html
    .replace(/<br\s*\/?>/gi, "\n")
    .replace(/<\/p>/gi, "\n")
    .replace(/<[^>]*>/g, "")
    .replace(/&nbsp;/g, " ")
    .replace(/&amp;/g, "&")
    .replace(/&lt;/g, "<")
    .replace(/&gt;/g, ">")
    .replace(/&quot;/g, '"')
    .replace(/\n{3,}/g, "\n\n")
    .trim();
}

function mapImage(img) {
  if (!img) return null;
  return {
    url: img.src,
    altText: img.alt || null,
    width: img.width || null,
    height: img.height || null,
  };
}

function mapVariant(v, product) {
  // Pair option1/2/3 with the parent product.options[i].name to produce
  // [{name, value}] just like the Storefront API does.
  const selectedOptions = (product.options || [])
    .map((opt, idx) => {
      const value = v[`option${idx + 1}`];
      if (value == null) return null;
      return { name: opt.name, value };
    })
    .filter(Boolean);

  return {
    id: String(v.id),
    title: v.title,
    availableForSale: !!v.available,
    quantityAvailable: null, // REST doesn't expose this
    selectedOptions,
    price: { amount: String(v.price), currencyCode: DEFAULT_CURRENCY },
    compareAtPrice: v.compare_at_price
      ? { amount: String(v.compare_at_price), currencyCode: DEFAULT_CURRENCY }
      : null,
    image: v.featured_image ? mapImage(v.featured_image) : null,
  };
}

function mapProduct(p) {
  const variants = (p.variants || []).map((v) => mapVariant(v, p));
  const images = (p.images || []).map(mapImage).filter(Boolean);
  const featuredImage = images[0] || null;

  const prices = variants.map((v) => parseFloat(v.price.amount));
  const min = prices.length ? Math.min(...prices) : 0;
  const max = prices.length ? Math.max(...prices) : 0;

  return {
    id: String(p.id),
    handle: p.handle,
    title: p.title,
    description: stripHtml(p.body_html),
    descriptionHtml: p.body_html || "",
    tags: p.tags || [],
    productType: p.product_type || "",
    vendor: p.vendor || "",
    availableForSale: variants.some((v) => v.availableForSale),
    totalInventory: null,
    options: (p.options || []).map((o) => ({
      id: `${p.id}-${o.name}`,
      name: o.name,
      values: o.values,
    })),
    featuredImage,
    images: { edges: images.map((n) => ({ node: n })) },
    priceRange: {
      minVariantPrice: { amount: min.toFixed(2), currencyCode: DEFAULT_CURRENCY },
      maxVariantPrice: { amount: max.toFixed(2), currencyCode: DEFAULT_CURRENCY },
    },
    variants: { edges: variants.map((n) => ({ node: n })) },
  };
}

/* -------------------------------- Products -------------------------------- */

let _productCache = null;

async function fetchAllProductsRaw() {
  if (_productCache) return _productCache;
  const res = await fetch(`${PRODUCTS_URL}?limit=250`, { cache: "no-store" });
  if (!res.ok) {
    throw new Error(`Shopify products.json returned ${res.status}`);
  }
  const json = await res.json();
  const products = (json.products || []).map(mapProduct);
  _productCache = products;
  return products;
}

export async function getAllProducts() {
  return fetchAllProductsRaw();
}

export async function getProductByHandle(handle) {
  const all = await fetchAllProductsRaw();
  return all.find((p) => p.handle === handle) || null;
}

// Collections are not exposed via the public products.json endpoint, so we
// return an empty list. Store.jsx already hides the Collections filter
// section when this is empty.
export async function getAllCollections() {
  return [];
}

export async function getShopInfo() {
  return {
    name: "Veterans for Indiana",
    description: "",
    primaryDomain: { url: `https://${DOMAIN}` },
    paymentSettings: { currencyCode: DEFAULT_CURRENCY },
  };
}

/* ---------------------------------- Cart ---------------------------------- */
//
// Local cart, persisted to localStorage, shaped to look like a Storefront
// API Cart so the UI doesn't have to change.

function emptyCart() {
  return {
    id: `local-${Date.now()}`,
    checkoutUrl: "",
    totalQuantity: 0,
    cost: {
      subtotalAmount: { amount: "0.00", currencyCode: DEFAULT_CURRENCY },
      totalAmount: { amount: "0.00", currencyCode: DEFAULT_CURRENCY },
      totalTaxAmount: { amount: "0.00", currencyCode: DEFAULT_CURRENCY },
    },
    lines: { edges: [] },
  };
}

function readCart() {
  if (typeof window === "undefined") return emptyCart();
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return emptyCart();
    const parsed = JSON.parse(raw);
    return recomputeCart(parsed);
  } catch {
    return emptyCart();
  }
}

function writeCart(cart) {
  const next = recomputeCart(cart);
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
  } catch {
    /* storage full / disabled — ignore */
  }
  return next;
}

function buildCheckoutUrl(lines) {
  if (!lines.length) return "";
  const items = lines
    .map((l) => `${l.node.merchandise.id}:${l.node.quantity}`)
    .join(",");
  return `${CART_PERMALINK_BASE}/${items}`;
}

function recomputeCart(cart) {
  const edges = cart?.lines?.edges || [];
  const totalQuantity = edges.reduce((s, e) => s + (e.node.quantity || 0), 0);
  const subtotal = edges.reduce(
    (s, e) =>
      s +
      (e.node.quantity || 0) *
        parseFloat(e.node.merchandise.price?.amount || "0"),
    0
  );
  const amount = subtotal.toFixed(2);
  return {
    ...cart,
    totalQuantity,
    checkoutUrl: buildCheckoutUrl(edges),
    cost: {
      subtotalAmount: { amount, currencyCode: DEFAULT_CURRENCY },
      totalAmount: { amount, currencyCode: DEFAULT_CURRENCY },
      totalTaxAmount: { amount: "0.00", currencyCode: DEFAULT_CURRENCY },
    },
    lines: { edges },
  };
}

function buildLine({ variant, product, quantity }) {
  // Use the variant id as the stable line id (one line per variant).
  return {
    node: {
      id: `line-${variant.id}`,
      quantity,
      merchandise: {
        id: variant.id,
        title: variant.title,
        availableForSale: variant.availableForSale,
        selectedOptions: variant.selectedOptions || [],
        price: variant.price,
        image: variant.image || product.featuredImage || null,
        product: {
          id: product.id,
          handle: product.handle,
          title: product.title,
          featuredImage: product.featuredImage || null,
        },
      },
    },
  };
}

export async function cartCreate() {
  // Local-only: just return the persisted cart (or a fresh empty one).
  return readCart();
}

export async function cartGet() {
  return readCart();
}

// Accepts either the legacy shape `[{ merchandiseId, quantity }]` (won't have
// enough info to render) or the enriched shape
// `[{ variant, product, quantity }]`. The CartContext is updated to pass the
// enriched shape so we always have full line data locally.
export async function cartLinesAdd(_cartId, inputs) {
  const cart = readCart();
  const edges = [...cart.lines.edges];

  for (const input of inputs) {
    const { variant, product, quantity = 1 } = input;
    if (!variant || !product) continue;
    const lineId = `line-${variant.id}`;
    const existing = edges.find((e) => e.node.id === lineId);
    if (existing) {
      existing.node.quantity += quantity;
    } else {
      edges.push(buildLine({ variant, product, quantity }));
    }
  }

  return writeCart({ ...cart, lines: { edges } });
}

export async function cartLinesUpdate(_cartId, lines) {
  const cart = readCart();
  const edges = cart.lines.edges
    .map((e) => {
      const update = lines.find((l) => l.id === e.node.id);
      if (!update) return e;
      if (update.quantity <= 0) return null;
      return { node: { ...e.node, quantity: update.quantity } };
    })
    .filter(Boolean);
  return writeCart({ ...cart, lines: { edges } });
}

export async function cartLinesRemove(_cartId, lineIds) {
  const cart = readCart();
  const edges = cart.lines.edges.filter((e) => !lineIds.includes(e.node.id));
  return writeCart({ ...cart, lines: { edges } });
}

/* -------------------------------- Helpers --------------------------------- */

export function formatPrice(money) {
  if (!money) return "";
  const amount = parseFloat(money.amount);
  const code = money.currencyCode || DEFAULT_CURRENCY;
  try {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: code,
      minimumFractionDigits: amount % 1 === 0 ? 0 : 2,
    }).format(amount);
  } catch {
    return `$${amount.toFixed(2)}`;
  }
}
